'use client';
import { ChevronDownIcon, CloseIcon, PlusSquareIcon } from '@chakra-ui/icons';
import {
  Box, Button, Container, Drawer, DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { Affix, Collapse } from 'antd';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import CustomImage from '@/app/_components/custom-image/CustomImage';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import { ReactComponent as CheckIconGreen } from '@/app/_icons/Ck_green_tick_icon.svg';
import { ReactComponent as CheckIcon } from '@/app/_icons/tick-icon.svg';
import { desktop } from '@/constants/vars.constants';
import { useWindowSize } from '@/hooks/window-size.hook';
import { getImageLink } from '@/services/link.service';
import { ImageTransformationKeys } from '@/types/files/files.types';
import { SpecificationsPricesType } from '@/types/new-cars/new-cars.types';

import Styles from './VariantsModels.module.scss';

import './VariantModelStyle.scss';

type SpecificationType = Record<string, Record<string, string | boolean>>;

type Props = {
  specificationsPrices: SpecificationsPricesType[];
  allSpecificationsPrices: SpecificationsPricesType[];
  modelName: string;
  specifications: Record<string, SpecificationType>;
};

const imageKeys: ImageTransformationKeys = {
  desktopKey: 'new-car-compare-model-desktop',
  mobileKey: 'new-car-compare-model-mobile',
};

const renderPrice = (price: number | string | null | undefined) => {
  if (!price || price === 0 || price === '0') {
    return <Text className={Styles.titleXXl}>Price Coming Soon</Text>;
  }
  return <PriceFormat prefix="R" value={Number(price)} />;
};

const VariantsModels = ({
  specificationsPrices,
  allSpecificationsPrices,
  modelName,
  specifications,
}: Props) => {
  const [selectedModels, setSelectedModels] =
    useState<SpecificationsPricesType[]>(specificationsPrices);

  const [checkedSpecifications, setCheckedSpecifications] = useState<
    SpecificationType[]
  >([]);

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [isSticky] = useState(false);
  const compareModelsBlockRef = useRef<HTMLDivElement>(null);
  const collapseRef = useRef<HTMLDivElement>(null);

  const panel = Object.keys(Object.values(specifications)[0] || []);

  const { Panel } = Collapse;

  const handleModelSelect = (index: number, modelId: string) => {
    const newModel = allSpecificationsPrices.find(
      (spec) => spec.duoporta_model_id === modelId,
    );

    if (newModel) {
      const updatedModels = [...selectedModels];

      updatedModels[index] = newModel;
      setSelectedModels([...updatedModels]);
    }
  };

  const handleClearModel = (index: number) => {
    selectedModels.splice(index, 1);
    setSelectedModels([...selectedModels]);
  };

  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (compareModelsBlockRef.current) {
  //       const rect = compareModelsBlockRef.current.getBoundingClientRect();

  //       setIsSticky(rect.top <= 0);
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // }, []);

  useEffect(() => {
    const details = selectedModels.map((model) => {
      return specifications[model.duoporta_model_id] || {};
    });

    setCheckedSpecifications(details);
  }, [selectedModels, specifications]);

  const scroll = (id: string) => {
    setTimeout(() => {
      const link = document.getElementById(id);

      if (link) {
        link.click();
      }
    }, 300);
  };

  const headerForPanel = (name: string) => {
    return (
      <>
        <Box ref={collapseRef} className={Styles.panelHead}>
          <Text className={Styles.title}>
            {name
              .split(/(?=[A-Z])/)
              .join(' ')
              .toUpperCase()}
          </Text>
        </Box>
      </>
    );
  };

  const size = useWindowSize();

  const isMobile = (size?.width || 0) < desktop;

  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);
  const [isAddModelOpen, setIsAddModelOpen] = useState(false);
  const [unselectedModels, setUnselectedModels] = useState<
    SpecificationsPricesType[]
  >([]);

  const renderValue = (value: boolean | string | null | undefined) => {
    if (typeof value === 'boolean') {
      return <Text>{value ? <CheckIconGreen /> : <CloseIcon />}</Text>;
    }

    if (!value) {
      return (
        <Text>
          <CloseIcon />
        </Text>
      );
    }

    if (value.toLowerCase() === 'std') {
      return (
        <Text>
          <CheckIconGreen />
        </Text>
      );
    }

    return <Text>{value}</Text>;
  };

  useEffect(() => {
    updateUnselectedModels();
  }, [selectedModels, allSpecificationsPrices]);

  const updateUnselectedModels = () => {
    const unselected = allSpecificationsPrices.filter(
      (spec) =>
        !selectedModels.some(
          (selected) => selected.duoporta_model_id === spec.duoporta_model_id,
        ),
    );

    setUnselectedModels(unselected);
  };

  const renderModelCard = (
    model: SpecificationsPricesType | null,
    index: number,
  ) => (
    <Box className={Styles.modelCard} key={index}>
      {model ? (
        <>
          <Box className={Styles.modelCardImage}>
            <CustomImage
              imageKeys={imageKeys}
              imageSrc={getImageLink(model?.image_id) ?? ''}
              alt={model.title}
            />
          </Box>
          <Box className={Styles.modelCardContent}>
            <Box>
              <Text fontSize="xs">{modelName.toUpperCase()}</Text>
              <Button
                onClick={() => setOpenModalIndex(index)}
                className={Styles.menuButton}
                rightIcon={<ChevronDownIcon />}
                title={model.title
                  .toLowerCase()
                  .replace(`${modelName}`, '')
                  .toUpperCase()}
              >
                <Text textAlign="left" className={Styles.menuText}>
                  {model.title
                    .toLowerCase()
                    .replace('kia ', '')
                    .replace(`${modelName}`, '')
                    .toUpperCase()}
                </Text>
              </Button>
              {isMobile ? (
                <Drawer
                  isOpen={openModalIndex === index}
                  placement="bottom"
                  onClose={() => setOpenModalIndex(null)}
                  id={`modelSelectDrawer${index}`}
                >
                  <DrawerOverlay />
                  <DrawerContent>
                    <DrawerHeader>
                      <DrawerCloseButton />
                    </DrawerHeader>
                    <DrawerBody px={0}>
                      {allSpecificationsPrices.map((spec) => (
                        <Button
                          key={spec.duoporta_model_id}
                          onClick={() => {
                            handleModelSelect(index, spec.duoporta_model_id);
                            setOpenModalIndex(null);
                          }}
                          className="modelSelectBtn"
                          display={'flex'}
                          alignItems={'center'}
                          justifyContent={'start'}
                          borderRadius={0}
                          w={'100%'}
                          h={'auto'}
                          my={2}
                          py={4}
                        >
                          {spec.title}
                          {spec.duoporta_model_id ===
                            model.duoporta_model_id && (
                              <Box ml={2} className="checkIcon">
                                <CheckIcon />
                              </Box>
                            )}
                        </Button>
                      ))}
                    </DrawerBody>
                  </DrawerContent>
                </Drawer>
              ) : (
                <Modal
                  isCentered
                  onClose={() => setOpenModalIndex(null)}
                  isOpen={openModalIndex === index}
                  size={'sm'}
                  motionPreset="slideInBottom"
                  id={`modelSelectModal${index}`}
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>
                      <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody px={0}>
                      {allSpecificationsPrices.map((spec) => (
                        <Button
                          key={spec.duoporta_model_id}
                          onClick={() => {
                            handleModelSelect(index, spec.duoporta_model_id);
                            setOpenModalIndex(null);
                          }}
                          className={cn('modelSelectBtn', {
                            modelSelectedBtn:
                              spec.duoporta_model_id ===
                              model.duoporta_model_id,
                          })}
                          display={'flex'}
                          alignItems={'center'}
                          justifyContent={'start'}
                          w={'100%'}
                          h={'auto'}
                          my={2}
                          py={4}
                        >
                          {spec.title}
                          {spec.duoporta_model_id ===
                            model.duoporta_model_id && (
                              <Box ml={2} className="checkIcon">
                                <CheckIcon />
                              </Box>
                            )}
                        </Button>
                      ))}
                    </ModalBody>
                  </ModalContent>
                </Modal>
              )}
            </Box>
            <Box>
              <Box className={Styles.headingElement}>
                <Heading as="h3" className={Styles.titleXXl}>
                  {renderPrice(model.price)}
                </Heading>
              </Box>
            </Box>
          </Box>
          {selectedModels?.length > 2 && (
            <Button
              className={Styles.closeButton}
              onClick={() => handleClearModel(index)}
              padding={0}
              minW={'auto'}
            >
              <CloseIcon />
            </Button>
          )}
        </>
      ) : null}
    </Box>
  );

  return (
    <>
      <Box
        ref={compareModelsBlockRef}
        className={Styles.compareModelWrapper}
        position={'relative'}
        height={'auto'}
        display={{ base: 'flex', lg: 'initial' }}
        justifyContent={{ base: 'center', lg: 'initial' }}
      >
        <Box className={Styles.scrollContainer}>
          <Box className={Styles.compareModelHeader}>
            <Container maxW={'1280'} width={'100%'} px={{ base: 0, lg: 6 }}>
              <Box
                height={{ lg: 'auto' }}
                className={Styles.compareModelsBlockWrap}
              >
                <HStack
                  justifyContent={'flex-start'}
                  alignItems={'flex-start'}
                  gap={0}
                  mb={{ lg: 4 }}
                  className={cn(Styles.compareModelsBlock, {
                    [Styles.sticky as string]: isSticky,
                  })}
                >
                  {selectedModels.map((model, index) => (
                    <Box key={index} className={Styles.cardWithDivider}>
                      {renderModelCard(model, index)}{' '}
                    </Box>
                  ))}
                  {selectedModels.length < 4 && (
                    <Box
                      className={cn(
                        Styles.modelCard,
                        Styles.addModelCard,
                        Styles.cardWithDivider,
                      )}
                    >
                      <Box className={Styles.modelCardContent}>
                        <Button
                          onClick={() => setIsAddModelOpen(true)}
                          className={Styles.textLinkBtn}
                        >
                          <PlusSquareIcon h={6} w={6} />
                          <span className={Styles.textLinkBtnText}>
                            Add Model
                          </span>
                        </Button>
                        {isMobile ? (
                          <Drawer
                            isOpen={isAddModelOpen}
                            placement="bottom"
                            onClose={() => setIsAddModelOpen(false)}
                            id="modelSelectDrawerAddMobile"
                          >
                            <DrawerOverlay />
                            <DrawerContent>
                              <DrawerHeader>
                                <DrawerCloseButton />
                              </DrawerHeader>
                              <DrawerBody px={0}>
                                {unselectedModels.map((spec) => (
                                  <Button
                                    key={spec.duoporta_model_id}
                                    onClick={() => {
                                      handleModelSelect(
                                        selectedModels.length,
                                        spec.duoporta_model_id,
                                      );
                                      setIsAddModelOpen(false);
                                    }}
                                    className="modelSelectBtn"
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'start'}
                                    borderRadius={0}
                                    w={'100%'}
                                    h={'auto'}
                                    my={2}
                                    py={4}
                                  >
                                    {spec.title}
                                  </Button>
                                ))}
                              </DrawerBody>
                            </DrawerContent>
                          </Drawer>
                        ) : (
                          <Modal
                            isCentered
                            onClose={() => setIsAddModelOpen(false)}
                            isOpen={isAddModelOpen}
                            size={'sm'}
                            motionPreset="slideInBottom"
                            id="modelSelectModalAddDesktop"
                          >
                            <ModalOverlay />
                            <ModalContent>
                              <ModalHeader>
                                <ModalCloseButton />
                              </ModalHeader>
                              <ModalBody px={0}>
                                {unselectedModels.map((spec) => (
                                  <Button
                                    key={spec.duoporta_model_id}
                                    onClick={() => {
                                      handleModelSelect(
                                        selectedModels.length,
                                        spec.duoporta_model_id,
                                      );
                                      setIsAddModelOpen(false);
                                    }}
                                    className="modelSelectBtn"
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'start'}
                                    w={'100%'}
                                    h={'auto'}
                                    my={2}
                                    py={4}
                                  >
                                    {spec.title}
                                  </Button>
                                ))}
                              </ModalBody>
                            </ModalContent>
                          </Modal>
                        )}
                      </Box>
                    </Box>
                  )}
                </HStack>
              </Box>
              <Box
                className={cn(Styles.wrapper, {
                  [Styles.sticky as string]: isSticky,
                })}
              >
                <Box className={Styles.body}>
                  <Box className={Styles.scrollableContent}>
                    <Collapse
                      accordion
                      expandIcon={() => (
                        <ChevronDownIcon
                          w={5}
                          h={5}
                          className={'collapseIcon'}
                        />
                      )}
                      // onChange={(e) => {
                      //   if (e !== undefined) {
                      //     scroll('' + e);
                      //   }
                      // }}
                      activeKey={activeKey || undefined}
                      onChange={(key) => {
                        setActiveKey(key as string);
                        if (key !== undefined) {
                          scroll('' + key);
                        }
                      }}
                    >
                      {panel.map((key, index) => (
                        <Panel
                          // header={headerForPanel(key)}
                          header={<Affix>{headerForPanel(key)}</Affix>}
                          key={index}
                          className={Styles.customPanel}
                          showArrow={true}
                        >
                          {checkedSpecifications.map((el, n) => (
                            <Box
                              key={n}
                              className={cn(Styles.item, {
                                [Styles.show as string]: n === 0,
                              })}
                            >
                              {Object.keys(el[key] || {}).map((spec, index) => (
                                <Box
                                  key={index}
                                  className={Styles.elementTable}
                                >
                                  <Box className={Styles.titleTable}>
                                    <Text>{spec.replace('_', ' ')}</Text>
                                  </Box>

                                  <Box className={Styles.bodyTable}>
                                    {checkedSpecifications.map((val, n) => (
                                      <Box
                                        key={n}
                                        className={cn(
                                          Styles.w25,
                                          Styles.specification,
                                        )}
                                      >
                                        <Text>
                                          {renderValue(val[key]?.[spec])}
                                        </Text>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Panel>
                      ))}
                    </Collapse>
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default VariantsModels;
